/**
 * VBL Forecast Types for Vibration-Based Life Forecasting
 */

import { FailureFilters } from './filters';
import { TurbineToForecast } from './vbl';
import { TurbineWorkspaceMapping } from './turbineWorkspaceMapping';

/**
 * VBL-specific forecast project data structure
 */
export interface VblForecastProject {
  id: string;
  name: string;
  description?: string;
  sourceAnalysisWorkspaceId: string; // Links to VBL analysis workspace
  sourceAnalysisName: string; // For display

  // Forecast configuration
  configuration: {
    filters: FailureFilters; // Subset of analysis filters for population
    forecastParams: {
      horizonDays: number; // Forecast horizon in days (e.g., 365 for 1 year)
      intervalDays: number; // Reporting interval in days (e.g., 30 for monthly)
      startDate: string; // ISO date - when forecast begins
      useBootstrapSimulation: boolean; // Whether to use parametric bootstrap
      confidenceLevel: number; // Confidence level for intervals (0.90, 0.95, 0.99)
      includeConfidenceIntervals: boolean; // Whether to calculate confidence intervals
      bootstrapSamples: number; // Number of bootstrap samples (100-10000)
    };
  };

  // VBL Weibull parameters from source analysis
  vblParameters: {
    shape: number;   // Beta parameter
    scale: number;   // Eta parameter (in days)
    shapeCI?: {
      lower: number;
      upper: number;
    };
    scaleCI?: {
      lower: number;
      upper: number;
    };
    rSquared: number;
    standardErrors?: {
      shape: number;
      scale: number;
    };
  };

  // Turbines that need forecasting
  turbinesToForecast: TurbineToForecast[];

  // Workspace mappings for turbines
  turbineMappings: TurbineWorkspaceMapping[];

  // Calculated forecast results (cached)
  results?: VblForecastResult[];

  // Standard workspace metadata
  metadata: {
    userId: string;
    createdAt: string;
    updatedAt: string;
    version: number;
    tags?: string[];
  };
}

/**
 * Individual VBL forecast result for a specific turbine-component
 */
export interface VblForecastResult {
  turbine: TurbineToForecast;
  currentDegradationTime: number; // days since detection
  hasMapping: boolean; // whether turbine has workspace mapping
  workspaceMapping?: TurbineWorkspaceMapping;

  // Predicted failure information
  predictedFailureDate: string; // ISO date - median prediction
  confidenceInterval: {
    lower: string; // 5th percentile date
    upper: string; // 95th percentile date
  };

  // Risk assessment
  riskLevel: 'Low' | 'Medium' | 'High'; // Based on time to predicted failure
  timeToFailure: {
    days: number; // median days until failure
    confidenceInterval: {
      lower: number; // 5th percentile days
      upper: number; // 95th percentile days
    };
  };

  // Probability of failure over time horizons
  probabilityOfFailureBy: Array<{
    date: string; // ISO date
    days: number; // days from now
    probability: number; // probability of failure by this date
    confidenceInterval?: {
      lower: number; // lower bound probability
      upper: number; // upper bound probability
    };
  }>;

  // Calculation metadata
  simulationMetadata?: {
    bootstrapSamples: number;
    convergenceAchieved: boolean;
    calculatedAt: string; // ISO timestamp
    samplingMethod?: string; // 'simple-percentile', 'rejection', 'inverse-transform', 'hybrid', etc.
    rejectionEfficiency?: number; // Ratio of successful rejection samples (Monte Carlo only)
    survivalAdaptation?: { // Survival adaptation details (Simple Percentile only)
      applied: boolean;
      survivalFraction: number;
      survivalProbability: number;
      informationGain: number;
      adjustmentFactor: number;
    };
  };
}

/**
 * VBL forecast configuration options
 */
export interface VblForecastConfiguration {
  horizonDays: number;
  intervalDays: number;
  startDate: string;
  useBootstrapSimulation: boolean;
  confidenceLevel: number;
  includeConfidenceIntervals: boolean;
  bootstrapSamples: number;

  // Monte Carlo VBL is used exclusively

  // Monte Carlo VBL options (for probabilistic sampling)
  samplingMethod?: 'rejection' | 'inverse-transform' | 'hybrid';
  rejectionSampleLimit?: number; // Max attempts for rejection sampling
  useLogNormalScale?: boolean; // Whether to sample log(η) for positive scale
  correlationThreshold?: number; // Min correlation to use multivariate sampling

  // Survival adaptation settings
  useSurvivalAdaptation?: boolean;
  survivalAdaptationStrength?: number;
  earlyStageThreshold?: number;
}

/**
 * VBL forecast calculation request
 */
export interface VblForecastRequest {
  turbinesToForecast: TurbineToForecast[];
  vblParameters: VblForecastProject['vblParameters'];
  configuration: VblForecastConfiguration;
}

/**
 * VBL forecast calculation response
 */
export interface VblForecastResponse {
  results: VblForecastResult[];
  calculationMetadata: {
    totalTurbines: number;
    mappedTurbines: number;
    unmappedTurbines: number;
    calculationTime: number; // milliseconds
    parametersUsed: VblForecastProject['vblParameters'];
  };
}

/**
 * API request for saving VBL forecast workspace
 */
export interface SaveVblForecastRequest {
  workspace_name: string;
  description?: string;
  tags?: string[];
  owner: string;
  workspace_type: 'vbl_forecast';
  configuration: VblForecastProject['configuration'];
  vbl_parameters: VblForecastProject['vblParameters'];
  turbines_to_forecast: TurbineToForecast[];
  current_results: VblForecastResult[];
}

/**
 * API request for updating VBL forecast workspace
 */
export interface UpdateVblForecastRequest {
  workspace_id: string;
  workspace_name?: string;
  description?: string;
  tags?: string[];
  workspace_type: 'vbl_forecast';
  configuration: VblForecastProject['configuration'];
  vbl_parameters: VblForecastProject['vblParameters'];
  turbines_to_forecast: TurbineToForecast[];
  current_results: VblForecastResult[];
  updated_by: string;
}

/**
 * Risk level calculation based on time to failure
 */
export function calculateRiskLevel(daysToFailure: number): 'Low' | 'Medium' | 'High' {
  if (daysToFailure <= 30) return 'High';      // Less than 1 month
  if (daysToFailure <= 90) return 'Medium';    // 1-3 months
  return 'Low';                                // More than 3 months
}

/**
 * VBL forecast filtering interface
 */
export interface VblForecastFilters {
  sites: string[];
  turbineModels: string[];  // model_name
  componentsMajor: string[]; // major_component_name
  componentsMinor: string[]; // component_name (sub-component)
}

/**
 * Default VBL forecast filters
 */
export const DEFAULT_VBL_FORECAST_FILTERS: VblForecastFilters = {
  sites: [],
  turbineModels: [],
  componentsMajor: [],
  componentsMinor: []
};

/**
 * Default VBL forecast configuration
 */
export const DEFAULT_VBL_FORECAST_CONFIG: VblForecastConfiguration = {
  horizonDays: 365, // 1 year
  intervalDays: 30, // Monthly reporting
  startDate: new Date().toISOString(),
  useBootstrapSimulation: true,
  confidenceLevel: 0.90,
  includeConfidenceIntervals: true,
  bootstrapSamples: 10000, // Increased for stable convergence (< 1 day variance)

  // Monte Carlo VBL used exclusively for consistent forecasting

  // Monte Carlo VBL defaults
  samplingMethod: 'hybrid', // Best balance of accuracy and efficiency
  rejectionSampleLimit: 1000, // Reasonable limit for rejection sampling
  useLogNormalScale: true, // Better parameter constraint handling
  correlationThreshold: 0.1, // Use multivariate sampling for low correlation
};